import { useCallback, useRef, useState } from "react";
import friendshipService from "../services/friendshipService";

// Simple in-memory TTL cache for relationship status
const TTL_MS = 60_000;

export default function useFriendSearch() {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(new Set()); // userIds in-flight
  const [statusCache, setStatusCache] = useState(new Map()); // Reactive cache
  const searchIdRef = useRef(0); // Track current search to ignore stale updates

  const getCachedStatus = useCallback((userId) => {
    const entry = statusCache.get(userId);
    if (!entry) return undefined;
    if (Date.now() - entry.ts > TTL_MS) {
      // Lazy eviction: return undefined; we'll refresh on next fetch
      return undefined;
    }
    return entry.status;
  }, [statusCache]);

  const setCachedStatus = useCallback((userId, status) => {
    setStatusCache(prev => {
      const next = new Map(prev);
      next.set(userId, { status, ts: Date.now() });
      return next;
    });
  }, []);

  const fetchStatuses = useCallback(
    async (users, searchId) => {
      // Batch results to avoid many re-renders
      const updates = [];
      await Promise.all(
        users.map(async (u) => {
          if (getCachedStatus(u.id) !== undefined) return;
          try {
            const status = await friendshipService.checkFriendshipStatus(u.id);
            // Check if this is still the current search
            if (searchId === searchIdRef.current) {
              updates.push([u.id, status]);
            }
          } catch (error) {
            console.warn(`Failed to fetch status for ${u.id}:`, error);
            // Check if this is still the current search
            if (searchId === searchIdRef.current) {
              updates.push([u.id, "none"]); // conservative fallback
            }
          }
        })
      );
      
      // Apply all updates at once to minimize re-renders
      if (updates.length && searchId === searchIdRef.current) {
        setStatusCache(prev => {
          const next = new Map(prev);
          const now = Date.now();
          for (const [id, status] of updates) {
            next.set(id, { status, ts: now });
          }
          return next;
        });
      }
    },
    [getCachedStatus]
  );

  const search = useCallback(
    async (term) => {
      const value = term ?? searchTerm;
      if (term !== undefined) setSearchTerm(value);
      setError(null);

      // Increment search ID to track current search
      const searchId = ++searchIdRef.current;

      if (!value.trim()) {
        setResults([]);
        return [];
      }

      setLoading(true);
      try {
        const users = await friendshipService.searchUsers(value);
        // Check if this is still the current search
        if (searchId !== searchIdRef.current) return []; // Drop stale search
        
        setResults(users);
        // Fire off background status resolution (no need to block UI)
        fetchStatuses(users, searchId);
        return users;
      } catch (e) {
        setError(e.message || "Search failed");
        setResults([]);
        return [];
      } finally {
        if (searchId === searchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [searchTerm, fetchStatuses]
  );

  const sendRequest = useCallback(
    async (userId) => {
      if (sending.has(userId)) return false;
      setSending((prev) => new Set(prev).add(userId));
      setCachedStatus(userId, "requested"); // optimistic
      try {
        await friendshipService.sendFriendRequest(userId);
        return true;
      } catch (e) {
        // rollback on failure - set to "none" explicitly
        setCachedStatus(userId, "none");
        setError(e.message || "Failed to send request");
        return false;
      } finally {
        setSending((prev) => {
          const n = new Set(prev);
          n.delete(userId);
          return n;
        });
      }
    },
    [setCachedStatus, sending]
  );

  const cancelRequest = useCallback(
    async (userId) => {
      try {
        await friendshipService.cancelFriendRequest(userId);
        setCachedStatus(userId, "none");
      } catch (e) {
        setError(e.message || "Failed to cancel request");
      }
    },
    [setCachedStatus]
  );

  const getStatus = useCallback(
    (userId) => getCachedStatus(userId) ?? "loading",
    [getCachedStatus]
  );
  const isRequestPending = useCallback(
    (userId) => getStatus(userId) === "requested",
    [getStatus]
  );

  return {
    searchTerm,
    setSearchTerm,
    searchResults: results,
    loading,
    error,
    search,
    clearSearch: () => {
      searchIdRef.current++; // Cancel any pending status fetches
      setSearchTerm("");
      setResults([]);
      setLoading(false);
    },
    sendRequest,
    cancelRequest,
    getStatus,
    isRequestPending,
  };
}